import * as jose from 'jose';
import { z } from 'zod';
import { TokenError } from './error.js';
export * from './error.js';
const alg = 'EdDSA';
/** Verify a payload against a given schema */
function validatePayload(data, schema) {
    const result = schema.safeParse(data);
    if (!result.success) {
        console.error('Invalid token payload:');
        console.error(result.error.errors);
        throw TokenError('Invalid token payload', 'invalid-payload');
    }
    return result.data;
}
/** Sign a token using the EdDSA algorithm */
export async function sign(data, privateKey, options) {
    if (!data.exp)
        throw TokenError('Missing exp field in token data. Expiry must be set', 'invalid-input');
    if (data.exp > 9999999999)
        throw TokenError('Invalid expiry. Check the time is in unix seconds and not ms', 'invalid-input');
    const key = await jose.importPKCS8(privateKey, alg).catch((err) => {
        throw TokenError(err.message, 'invalid-key');
    });
    return await new jose.SignJWT(data)
        .setProtectedHeader({ alg })
        .setIssuedAt(options?.iat)
        .setExpirationTime(data.exp)
        .sign(key)
        .catch((err) => {
        throw TokenError(err.message, 'unknown');
    });
}
/** Decode a JWT token to see the claims */
export function decode(token, schema = z.any()) {
    return validatePayload(jose.decodeJwt(token), schema);
}
/** Verify a JWT using the EdDSA algorithm and verify the payload */
export async function verify(token, publicKey, schema) {
    const key = await jose.importSPKI(publicKey, alg).catch((err) => {
        throw TokenError(err.message, 'invalid-key');
    });
    const { payload } = await jose
        .jwtVerify(token, key, { algorithms: [alg] })
        .catch((err) => {
        if (err.code === 'ERR_JWT_EXPIRED')
            throw TokenError('Token has expired', 'expired');
        if (err.code === 'ERR_JWS_SIGNATURE_VERIFICATION_FAILED')
            throw TokenError('Invalid token signature', 'invalid-signature');
        throw TokenError(err.message, 'unknown');
    });
    return validatePayload(payload, schema);
}
